import { DatePipe } from '@angular/common';
import { byDate, getMax, getMin, nilFirst, nilLast } from './sort-utils';

export function isInvalidDate(x: unknown): boolean {
    return x instanceof Date && isNaN(x.getTime());
}

export function getPvaMinDate(): Date {
    return new Date('1111-11-11T00:00:00');
}
export function getPvaMaxDate(): Date {
    return new Date('2800-12-31T00:00:00');
}

function filterOutInvalidDates(dates: Array<Date | null | undefined>): Array<Date | null | undefined> {
    return dates.filter((x) => !isInvalidDate(x));
}

/**
 * Liefert das größte Datum aus einer Liste von Datumswerten zurück.
 * null und undefined werden als max Date interpretiert, wenn die Liste also null
 * oder undefined enthält, wird null zurückgegeben.
 * Invalid Dates werden ignoriert.
 */
export function getMaxDateOrNull(dates: Array<Date | null | undefined>): Date | null {
    return getMax(filterOutInvalidDates(dates), nilLast(byDate)) ?? null;
}

/**
 * Liefert das kleinste Datum aus einer Liste von Datumswerten zurück.
 * null und undefined werden als min Date interpretiert, wenn die Liste also null
 * oder undefined enthält, wird null zurückgegeben.
 * Invalid Dates werden ignoriert.
 */
export function getMinDateOrNull(dates: Array<Date | null | undefined>): Date | null {
    return getMin(filterOutInvalidDates(dates), nilFirst(byDate)) ?? null;
}

/**
 * Liefert die Kalenderwoche nach ISO 8601 zurück
 * Source: https://weeknumber.ie/how-to/javascript
 */
export function getWeekNumber(dateInput: Date): number {
    const date = new Date(dateInput.getTime());
    date.setHours(0, 0, 0, 0);
    // Thursday in current week decides the year.
    date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
    // January 4 is always in week 1.
    const week1 = new Date(date.getFullYear(), 0, 4);
    // Adjust to Thursday in week 1 and count number of weeks from date to week1.
    return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
}

// entspricht Platform HistorizedValidationHelper.isValid
// dto.gueltigVon === null wird als MIN_DATE und dto.gueltigBis === null wird als MAX_DATE interpretiert
export function isValid(dto: { gueltigVon: Date | null; gueltigBis: Date | null }, atDate: Date = new Date()): boolean {
    const vonField: Date | null = dto.gueltigVon;
    const bisField: Date | null = dto.gueltigBis;

    const datum: Date = atStartOfDay(atDate);
    const von: Date | null = atStartOfDay(vonField);
    const bis: Date | null = atStartOfDay(bisField);

    return !(von && von > datum) && !(bis && bis < datum);
}

// entspricht ZeitraumServiceImpl.hasUeberlappung
// gueltigVon === null wird als MIN_DATE und gueltigBis === null wird als MAX_DATE interpretiert
export function hasUeberlappung(a: { gueltigVon: Date | null; gueltigBis: Date | null }, b: { gueltigVon: Date | null; gueltigBis: Date | null }): boolean {
    const aVon: Date | null = atStartOfDay(a.gueltigVon);
    const aBis: Date | null = atStartOfDay(a.gueltigBis);
    const bVon: Date | null = atStartOfDay(b.gueltigVon);
    const bBis: Date | null = atStartOfDay(b.gueltigBis);

    return (aBis === null || bVon === null || aBis >= bVon) && (aVon === null || bBis === null || aVon <= bBis);
}

export function atStartOfDay<T extends Date | null | undefined>(date: T): T extends Date ? Date : T extends null ? null : undefined;
export function atStartOfDay(date: Date | null | undefined): Date | null | undefined {
    if (!date) {
        return date;
    }
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
}

export function getDateToday(): Date {
    return atStartOfDay(new Date());
}

export function getDateTomorrow(): Date {
    return addDays(getDateToday(), 1);
}

export function getDateMinusDays(date: Date, days: number): Date {
    return addDays(date, days * -1);
}

export function getDatePlusDays(date: Date, days: number): Date {
    return addDays(date, days);
}

function addDays(date: Date, days: number): Date {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + days);
    return newDate;
}

export function dateIstAfterToday(date?: Date | null): boolean {
    if (!date) {
        return false;
    }
    const today = getDateToday();
    return date > today;
}

export function dateIsBeforeToday(date?: Date | null): boolean {
    if (!date) {
        return false;
    }
    const today = getDateToday();
    return date < today;
}

export function getLastDayOfMonth(year: number | null | undefined, month: number | null | undefined): Date | null {
    if (year == null || month == null) {
        return null;
    }
    return new Date(year, month + 1, 0);
}

export function getDatePlusMonths(date: Date | null | undefined, months: number): Date | null {
    if (!date) {
        return null;
    }
    const newDate = new Date(date);
    newDate.setMonth(newDate.getMonth() + months);
    return newDate;
}

export function isSameDay(date1: Date | null | undefined, date2: Date | null | undefined): boolean {
    if (!date1 || !date2) {
        return false;
    }
    return atStartOfDay(date1).getTime() === atStartOfDay(date2).getTime();
}

export function mapDateToDateString(date: Date | null, dateFormat = 'yyyy-MM-dd'): string | undefined {
    if (!date) {
        return;
    }
    const datePipe = new DatePipe('de-AT');
    return datePipe.transform(date, dateFormat) ?? undefined;
}
